import { type SyntheticEvent, type Context, createContext } from 'react';

import type { ValidationState } from '../../validation/types.js';

/** @internal */
export type ToggleButtonGroupContextType = {
  /** The name of the ToggleButtonGroup. */
  name?: string;
  /**
   * Internal: associates the group's items with a `_Form` rendered outside
   * their React subtree via the native `form` attribute. Unstable.
   * @internal
   */
  form?: string;
  /**
   * Whether the ToggleButtonGroup is required.
   * @defaultValue false
   * */
  isRequired?: boolean;
  /** Whether the ToggleButtonGroup is invalid. */
  hasError?: boolean;
  /** Whether the ToggleButtonGroup is disabled. */
  isDisabled?: boolean;
  /** Whether the ToggleButtonGroup is read-only. */
  isReadOnly?: boolean;
  /** Whether the ToggleButtonGroup is aria-disabled. */
  ariaDisabled?: boolean;
  /** The selected value of the ToggleButtonGroup. */
  selectedValue?: string | null;
  /** Handler that is called when the value of the group changes. */
  onChange?: (
    value: string,
    event?: SyntheticEvent<HTMLElement | SVGElement>,
  ) => void;
  /** Called whenever the group's validation state changes due to a user interaction. */
  onValidityChange?: (state: ValidationState) => void;
};

/**
 * Context to hold the state of the ToggleButtonGroup
 * @internal
 */
export const ToggleButtonGroupContext: Context<ToggleButtonGroupContextType> =
  createContext<ToggleButtonGroupContextType>({});
